// game/client/src/hud/offscreen-hero.ts
//
// Off-screen hero indicator (issue #5 camera recovery, 2026-06-18). When the
// camera is parked/peeked away from the local kobold and the hero leaves the
// frame, a small gold-rim badge pins to the screen edge with an arrow pointing
// back toward the hero. It's also clickable → recenter (mirrors Space / the
// portrait click), so a parked camera is never a dead end.
//
// main.ts feeds it the hero's NDC each frame (or null to hide); this module owns
// only the DOM + the edge-projection math. Kept presentational + dependency-free
// to match the other hud/ widgets.

const STYLE_ID = "offscreen-hero-style";
const CSS = `
.offhero {
  position: fixed; left: 0; top: 0; z-index: 30; display: none;
  width: 40px; height: 40px; margin: -20px 0 0 -20px;  /* center on (left,top) */
  pointer-events: auto; cursor: pointer;
  border-radius: 50%;
  background: radial-gradient(circle at 50% 38%, rgba(40,34,22,0.96), rgba(12,10,7,0.96));
  border: 1.5px solid rgba(247, 200, 110, 0.85);
  box-shadow: 0 0 12px rgba(247, 195, 96, 0.45), 0 2px 6px rgba(0,0,0,0.6);
  animation: offhero-pulse 1.4s ease-in-out infinite;
}
.offhero.show { display: block; }
.offhero__arrow {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  color: #f7c360;
}
.offhero__arrow svg { width: 22px; height: 22px; display: block; }
.offhero__dot {
  position: absolute; left: 50%; top: 50%; width: 8px; height: 8px; margin: -4px 0 0 -4px;
  border-radius: 50%; background: #f7c360; box-shadow: 0 0 5px rgba(247,195,96,0.9);
}
@keyframes offhero-pulse {
  0%, 100% { box-shadow: 0 0 9px rgba(247,195,96,0.40), 0 2px 6px rgba(0,0,0,0.6); }
  50%      { box-shadow: 0 0 16px rgba(247,195,96,0.75), 0 2px 6px rgba(0,0,0,0.6); }
}
`;

export interface OffscreenHero {
  /** Feed the hero's NDC (x right, y up). Pass null to hide (hero on-screen). */
  update(ndc: { nx: number; ny: number } | null): void;
  dispose(): void;
}

/** Inset from the viewport edge (px) where the badge rides. */
const EDGE_INSET = 34;

export function createOffscreenHero(onClick: () => void): OffscreenHero {
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    document.head.appendChild(style);
  }

  const root = document.createElement("div");
  root.className = "offhero";
  // Arrow points to the RIGHT by default (angle 0); we rotate the whole badge so
  // the arrow aims outward toward the hero. A centered dot reads as "your hero".
  root.innerHTML = `
    <div class="offhero__arrow"><svg viewBox="0 0 24 24"><path d="M4 12 H17 M12 6 L18 12 L12 18" stroke="currentColor" stroke-width="2.4" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg></div>
    <div class="offhero__dot"></div>
  `;
  root.title = "Your hero — click to recenter (Space)";
  root.addEventListener("click", () => onClick());
  document.body.appendChild(root);

  const arrow = root.querySelector<HTMLDivElement>(".offhero__arrow")!;

  function update(ndc: { nx: number; ny: number } | null): void {
    if (!ndc) {
      if (root.classList.contains("show")) root.classList.remove("show");
      return;
    }
    const w = window.innerWidth;
    const h = window.innerHeight;
    // NDC → screen-space direction from center (NDC y is up, screen y is down).
    let sx = ndc.nx;
    let sy = -ndc.ny;
    const len = Math.hypot(sx, sy) || 1;
    sx /= len; sy /= len;

    // Cast the direction ray to the inset viewport rectangle.
    const halfW = w / 2 - EDGE_INSET;
    const halfH = h / 2 - EDGE_INSET;
    const tx = sx !== 0 ? halfW / Math.abs(sx) : Infinity;
    const ty = sy !== 0 ? halfH / Math.abs(sy) : Infinity;
    const t = Math.min(tx, ty);
    const px = w / 2 + sx * t;
    const py = h / 2 + sy * t;

    root.style.left = `${px}px`;
    root.style.top = `${py}px`;
    arrow.style.transform = `rotate(${Math.atan2(sy, sx)}rad)`;
    if (!root.classList.contains("show")) root.classList.add("show");
  }

  function dispose(): void {
    root.remove();
  }

  return { update, dispose };
}
